import type { FC } from 'react';

import { ClickableDiv } from '@wwe-portal/ui/components/button';
import { SoldOut } from '@wwe-portal/ui/features/store-revamp';
import lockIconWebp from '@wwe-portal/ui/assets/images/flagship/lock-icon.webp';
import { cn } from '@wwe-portal/ui/lib/lib';
import { useTimeLeft } from '@wwe-portal/ui/lib/hooks';
import { IcoMoonIcon } from '@wwe-portal/ui/components/icons/icomoon';
import { OfferCardTypeEnum } from '@wwe-portal/entities/offer.type';

import { NotifPip } from '../flagship/notif-pip';
import { DEFAULT_DRIP_DAYS, type OfferCardProps } from '../type';
import { OfferHeader } from './offer-header';
import { OfferItems } from './offer-items';
import { ResponseOfferCardBackground } from './responsive-offer-card-background';

const ACCENT_TEXT_STYLE = {
  color: '#FBB300',
} as const;

type DripOfferSingle1CardProps = Pick<
  OfferCardProps,
  | 'id'
  | 'backgroundImage'
  | 'dynamicBackgroundImage'
  | 'title'
  | 'price'
  | 'strikethroughPrice'
  | 'endDate'
  | 'items'
  | 'style'
  | 'onClick'
  | 'onPurchaseClick'
  | 'overlay'
  | 'hasPip'
  | 'type'
  | 'isBlocked'
  | 'soldOut'
  | 'totalItems'
  | 'dripDays'
  | 'dripLabels'
>;

export const DripOfferSingle1Card: FC<DripOfferSingle1CardProps> = ({
  id,
  backgroundImage,
  dynamicBackgroundImage,
  title,
  price,
  strikethroughPrice,
  endDate,
  items,
  style,
  onClick,
  onPurchaseClick,
  overlay,
  hasPip,
  type,
  isBlocked = false,
  soldOut,
  totalItems,
  dripDays,
  dripLabels,
}) => {
  const { getTimeText } = useTimeLeft(endDate ?? new Date());
  const timeText = getTimeText(' ');

  const effectiveDripDays = dripDays ?? DEFAULT_DRIP_DAYS;
  const labels = {
    offerEndsIn: dripLabels?.offerEndsIn ?? 'OFFER ENDS IN:',
    getTotalOf: dripLabels?.getTotalOf ?? 'GET A TOTAL OF',
    overDays:
      dripLabels?.overDays?.replace('{days}', String(effectiveDripDays)) ??
      `OVER ${effectiveDripDays} DAYS`,
  };

  return (
    <div className="relative" data-id={id}>
      <ClickableDiv
        className={cn(
          'aspect-single-1 font-poppins',
          'rounded-lg shadow-[0_0_20px_rgba(217,70,239,0.6)] ring-2 ring-fuchsia-500'
        )}
        onClick={onClick}
        style={style}
      >
        <div className="absolute inset-0 overflow-clip rounded-lg">
          <div className="absolute -bottom-[calc(700%/399)] -left-[calc(700%/712)] -right-[calc(700%/712)] -top-[calc(700%/399)]">
            <ResponseOfferCardBackground
              backgroundImage={backgroundImage}
              dynamicBackgroundImage={dynamicBackgroundImage}
              type={type}
            />
          </div>
        </div>
        <div className="absolute inset-0 flex flex-col px-2 py-2">
          <div className="text-center">
            <OfferHeader maxFont={16}>{title}</OfferHeader>
          </div>

          {endDate && timeText && (
            <div className="mt-1 flex justify-center">
              <div className="inline-flex items-center gap-1.5 rounded bg-black/50 px-3 py-1">
                <span className="text-2xs font-bold uppercase tracking-wide text-cyan-400">
                  {labels.offerEndsIn}
                </span>
                <IcoMoonIcon
                  className="h-3.5 w-3.5 text-cyan-400"
                  iconName="timer"
                />
                <span className="text-2xs -ml-1 font-bold text-cyan-400">
                  {timeText}
                </span>
              </div>
            </div>
          )}

          <div className="mb-2 mt-2 text-center">
            <span
              className="text-xs font-bold uppercase italic tracking-wide drop-shadow-lg"
              style={ACCENT_TEXT_STYLE}
            >
              {labels.getTotalOf}
            </span>
          </div>

          <div className="relative flex-1">
            <div className="absolute inset-0 flex items-center justify-center">
              <OfferItems
                itemWidth="calc(4000%/348)"
                items={items}
                maxItem={3}
                totalItems={totalItems}
              />
            </div>
          </div>

          <div className="mt-2 text-center">
            <span
              className="text-xs font-bold uppercase italic tracking-wide drop-shadow-lg"
              style={ACCENT_TEXT_STYLE}
            >
              {labels.overDays}
            </span>
          </div>

          {price != null && (
            <ClickableDiv
              className="mt-1 flex items-center justify-center"
              data-slot="price"
              onClick={onPurchaseClick}
            >
              <div className="drop-shadow-text-2 flex flex-row items-center justify-center space-x-[5px] font-semibold text-white">
                {strikethroughPrice ? (
                  <span className="pr-[2px] text-left text-xs line-through drop-shadow-lg">
                    {strikethroughPrice}
                  </span>
                ) : null}
                <span className="flex items-center justify-center gap-x-1 text-center text-base font-semibold capitalize">
                  {price}
                </span>
              </div>
              {Boolean(isBlocked) && (
                <img
                  alt="locked"
                  className="ml-1 h-[21px] md:h-[22px]"
                  src={lockIconWebp}
                />
              )}
            </ClickableDiv>
          )}
        </div>
        {overlay}
      </ClickableDiv>
      {hasPip && !soldOut ? <NotifPip /> : null}
      {soldOut && <SoldOut type={OfferCardTypeEnum.SINGLE1} />}
    </div>
  );
};
